import type { Chart, LineElement, Plugin, PointElement } from 'chart.js';

const PRICE_DATASET_INDEX = 0;
const FILL_ALPHA = 0.05;
const SHADOW_ALPHA = 0.2;
const SHADOW_OFFSET_Y = 4;
const SHADOW_BLUR = 'blur(2px)';

const offscreenCanvases = new WeakMap<Chart, HTMLCanvasElement>();

function getOffscreenContext(chart: Chart) {
  const canvas = offscreenCanvases.get(chart) ?? document.createElement('canvas');
  offscreenCanvases.set(chart, canvas);
  canvas.width = chart.canvas.width;
  canvas.height = chart.canvas.height;

  const ctx = canvas.getContext('2d');
  ctx?.setTransform(chart.currentDevicePixelRatio, 0, 0, chart.currentDevicePixelRatio, 0, 0);

  return ctx;
}

export const stockPriceLinePlugin: Plugin<'line'> = {
  id: 'stockPriceLine',
  beforeDatasetsDraw(chart) {
    const meta = chart.getDatasetMeta(PRICE_DATASET_INDEX);
    const line = meta.dataset as LineElement | undefined;
    const points = (meta.data as PointElement[]).filter((point) => !point.skip);

    if (!meta.visible || !line || points.length < 2) {
      return;
    }

    const ctx = getOffscreenContext(chart);

    if (!ctx) {
      return;
    }

    const { chartArea } = chart;
    const top = points.reduce((min, point) => Math.min(min, point.y), chartArea.bottom);

    ctx.fillStyle = line.options.borderColor;

    for (const segment of line.segments) {
      const first = line.points[segment.start];
      const last = line.points[segment.end];

      if (first.x === null || last.x === null) {
        continue;
      }

      ctx.beginPath();
      line.pathSegment(ctx, segment, {});
      ctx.lineTo(last.x, chartArea.bottom);
      ctx.lineTo(first.x, chartArea.bottom);
      ctx.closePath();
      ctx.fill();
    }

    const mask = ctx.createLinearGradient(0, top, 0, chartArea.bottom);
    mask.addColorStop(0, `rgba(255, 255, 255, ${FILL_ALPHA})`);
    mask.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.globalCompositeOperation = 'destination-in';
    ctx.fillStyle = mask;
    ctx.fillRect(0, 0, chart.width, chart.height);

    chart.ctx.drawImage(ctx.canvas, 0, 0, chart.width, chart.height);
  },
  beforeDatasetDraw(chart, { index, meta }) {
    const { ctx } = chart;

    if (index !== PRICE_DATASET_INDEX || !('filter' in ctx)) {
      return;
    }

    const line = meta.dataset as LineElement;

    ctx.save();
    ctx.translate(0, SHADOW_OFFSET_Y);
    ctx.filter = SHADOW_BLUR;
    ctx.globalAlpha = SHADOW_ALPHA;
    ctx.lineWidth = line.options.borderWidth;
    ctx.strokeStyle = line.options.borderColor;
    ctx.beginPath();
    line.path(ctx);
    ctx.stroke();
    ctx.restore();
  },
};
